// react
import { useCallback, useEffect, useMemo } from 'react';

// relative
import useSearchParams from './useStableSearchParams';

import { DateTime } from 'luxon';

const FROM_PARAM = 'fromComparison';
const TO_PARAM = 'toComparison';

function createDefaultRange({ zone = 'Europe/Copenhagen', days = 7 } = {}) {
  const nowLocal = DateTime.now().setZone(zone);
  const start = nowLocal.minus({ days }).startOf('day');
  const end = nowLocal.endOf('day');
  return [start, end];
}

function parseRangeFromParams({ searchParams, fromKey, toKey, zone }) {
  const fromParam = searchParams.get(fromKey);
  const toParam = searchParams.get(toKey);

  if (!fromParam || !toParam) return null;

  const from = DateTime.fromISO(fromParam).setZone(zone);
  const to = DateTime.fromISO(toParam).setZone(zone);

  if (!from.isValid || !to.isValid) return null;

  return [from, to];
}

export function useUrlComparisonDateRange(options = {}) {
  const {
    fromKey = FROM_PARAM,
    toKey = TO_PARAM,
    zone = 'Europe/Copenhagen',
    defaultDays = 7,
  } = options;

  const [searchParams, setSearchParams] = useSearchParams();

  // URL is the source of truth: derive range from URL if valid, else fallback default
  const comparisonRange = useMemo(() => {
    const parsed = parseRangeFromParams({ searchParams, fromKey, toKey, zone });
    if (parsed) return parsed;
    return createDefaultRange({ zone, days: defaultDays });
  }, [searchParams, fromKey, toKey, zone, defaultDays]);

  const [fromComparison, toComparison] = comparisonRange;

  // External sync: ensure URL always has valid values (defaults/repair)
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);

        const parsed = parseRangeFromParams({
          searchParams: next,
          fromKey,
          toKey,
          zone,
        });
        if (parsed) return prev; // URL is valid, no update needed
        const [start, end] = createDefaultRange({ zone, days: defaultDays });

        const startISO = start.toISO();
        const endISO = end.toISO();

        const same =
          next.get(fromKey) === startISO && next.get(toKey) === endISO;
        if (same) return prev;
        next.set(fromKey, startISO);
        next.set(toKey, endISO);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams, fromKey, toKey, zone, defaultDays]);

  // Function to update range & URL (no local state to update)
  const setComparisonRange = useCallback(
    (nextFrom, nextTo, { roundToDay = true, clampToNow = true } = {}) => {
      if (!nextFrom || !nextTo) return;

      let start = nextFrom.setZone(zone);
      let end = nextTo.setZone(zone);

      if (roundToDay) {
        start = start.startOf('day');
        end = end.endOf('day');
      }

      const now = DateTime.now().setZone(zone);
      if (clampToNow && end > now) {
        end = now;
      }

      if (end < start) return;

      const startISO = start.toISO();
      const endISO = end.toISO();

      setSearchParams((prev) => {
        const same =
          prev.get(fromKey) === startISO && prev.get(toKey) === endISO;

        if (same) return prev;

        const next = new URLSearchParams(prev);
        next.set(fromKey, startISO);
        next.set(toKey, endISO);
        console.log('109');
        return next;
      });
    },
    [setSearchParams, fromKey, toKey, zone],
  );

  // Derived values
  const comparisonTimeRange = useMemo(() => {
    if (!fromComparison || !toComparison) return null;
    return {
      start: fromComparison.toUTC().toMillis(),
      end: toComparison.toUTC().toMillis(),
    };
  }, [fromComparison, toComparison]);

  const comparisonDurationDays = useMemo(() => {
    if (!fromComparison || !toComparison) return null;
    return Math.round(toComparison.diff(fromComparison, 'days').days);
  }, [fromComparison, toComparison]);

  return {
    fromComparison,
    toComparison,
    comparisonRange,
    setComparisonRange,
    comparisonTimeRange,
    comparisonDurationDays,
  };
}

// import { useCallback, useEffect, useMemo, useState } from 'react';
// import { useSearchParams } from 'react-router-dom';
// import { DateTime } from 'luxon';

// export const FROM_PARAM = 'fromComparison';
// export const TO_PARAM = 'toComparison';

// function createDefaultRange({ zone = 'Europe/Copenhagen', days = 7 } = {}) {
//   const nowLocal = DateTime.now().setZone(zone);
//   const start = nowLocal.minus({ days }).startOf('day');
//   const end = nowLocal.endOf('day');
//   return [start, end];
// }

// export function useUrlComparisonDateRange(options = {}) {
//   const {
//     fromKey = FROM_PARAM,
//     toKey = TO_PARAM,
//     zone = 'Europe/Copenhagen',
//     defaultDays = 7,
//   } = options;

//   const [searchParams, setSearchParams] = useSearchParams();

//   // --- Initial range: try from URL, else fallback ---
//   const [comparisonRange, setComparisonRangeState] = useState(() => {
//     const fromParam = searchParams.get(fromKey);
//     const toParam = searchParams.get(toKey);

//     if (fromParam && toParam) {
//       const from = DateTime.fromISO(fromParam).setZone(zone);
//       const to = DateTime.fromISO(toParam).setZone(zone);

//       if (from.isValid && to.isValid) {
//         return [from, to];
//       }
//     }

//     return createDefaultRange({ zone, days: defaultDays });
//   });

//   // --- Sync with URL changes ---
//   useEffect(() => {
//     const fromParam = searchParams.get(fromKey);
//     const toParam = searchParams.get(toKey);

//     const setDefaultRangeInUrl = () => {
//       const [start, end] = createDefaultRange({ zone, days: defaultDays });
//       setComparisonRangeState([start, end]);
//       const next = new URLSearchParams(searchParams);
//       next.set(fromKey, start.toISO());
//       next.set(toKey, end.toISO());
//       setSearchParams(next, { replace: true });
//     };

//     if (!fromParam || !toParam) {
//       const [currentFrom, currentTo] = comparisonRange;
//       const next = new URLSearchParams(searchParams);
//       next.set(fromKey, currentFrom.toISO());
//       next.set(toKey, currentTo.toISO());
//       setSearchParams(next, { replace: true });
//       return;
//     }

//     const from = DateTime.fromISO(fromParam).setZone(zone);
//     const to = DateTime.fromISO(toParam).setZone(zone);

//     if (!from.isValid || !to.isValid) {
//       setDefaultRangeInUrl();
//       return;
//     }

//     setComparisonRangeState((prev) => {
//       const [prevFrom, prevTo] = prev || [];
//       const isSame =
//         prevFrom &&
//         prevTo &&
//         prevFrom.toISO() === from.toISO() &&
//         prevTo.toISO() === to.toISO();

//       return isSame ? prev : [from, to];
//     });
//   }, [
//     searchParams,
//     setSearchParams,
//     fromKey,
//     toKey,
//     zone,
//     defaultDays,
//     comparisonRange,
//   ]);

//   // --- Function to update range & URL ---
//   const setComparisonRange = useCallback(
//     (nextFrom, nextTo, { roundToDay = true, clampToNow = true } = {}) => {
//       if (!nextFrom || !nextTo) return;

//       let start = nextFrom;
//       let end = nextTo;

//       if (roundToDay) {
//         start = start.startOf('day');
//         end = end.endOf('day');
//       }

//       const now = DateTime.now().setZone(zone);
//       if (clampToNow && end > now) {
//         end = now;
//       }

//       if (end < start) return;

//       setComparisonRangeState([start, end]);

//       const next = new URLSearchParams(searchParams);
//       next.set(fromKey, start.toISO());
//       next.set(toKey, end.toISO());
//       setSearchParams(next);
//     },
//     [searchParams, setSearchParams, fromKey, toKey, zone],
//   );

//   const [fromComparison, toComparison] = comparisonRange;

//   // --- Derived values: UTC millis + duration ---
//   const comparisonTimeRange = useMemo(() => {
//     if (!fromComparison || !toComparison) return null;
//     return {
//       start: fromComparison.toUTC().toMillis(),
//       end: toComparison.toUTC().toMillis(),
//     };
//   }, [fromComparison, toComparison]);

//   const comparisonDurationDays = useMemo(() => {
//     if (!fromComparison || !toComparison) return null;
//     return Math.round(toComparison.diff(fromComparison, 'days').days);
//   }, [fromComparison, toComparison]);

//   return {
//     fromComparison,
//     toComparison,
//     comparisonRange, // [fromComparison, toComparison]
//     setComparisonRange, // (fromDateTime, toDateTime, options?)
//     comparisonTimeRange, // { start, end } in UTC millis
//     comparisonDurationDays, // integer (e.g. 7)
//   };
// }
